import { Link } from 'react-router-dom';
import Button from '@/components/Button.tsx';
import { ArrowLeft, ArrowRight, Close, InfoCircle } from '@/components/Icons.tsx';
import { ReactNode, useState } from 'react';
import Popover from '@/components/Popover.tsx';
import useHorizontalRow from '@/hooks/useHorizontalRow.ts';

const Pricing = () => (
  <section id="pricing-section" className="bg-common-white text-[#151012] pt-24 pb-14 md:pb-24 text-center px-4">
    <h3 className="font-primary text-title md:text-heroTitle !font-extraBold">
      Choose what's right for you
    </h3>

    <PricingTable className="mt-24 mb-10 md:mb-16" />

    <Link to="/sign-in" className="m:inline-flex">
      <Button className="px-3 md:px-6 py-2 md:py-5 rounded-full m:h-11" type="button">GET STARTED NOW</Button>
    </Link>
  </section>
);

interface PricingTableProps {
  className?: string;
  /**
   * Content for a closing row under each plan (the packages page puts the live price and
   * the checkout button there). Without it, each plan shows its list price in the header.
   */
  renderFooter?: (product: ProductProps) => ReactNode;
  /**
   * Size the table for the authenticated layout's column (at most 40rem) instead of the
   * landing page. Compact sizing follows the nearest `@container`, not the viewport: all
   * four plans fit a full-width column, and narrower ones page like the phone layout.
   */
  compact?: boolean;
}

export const PricingTable = ({ className = '', renderFooter, compact = false }: PricingTableProps) => {
  const { rowRef, isScrollable, isAtStart, isAtEnd, scrollPrev, scrollNext } = useHorizontalRow();
  const sizes = compact ? COMPACT_SIZES : DEFAULT_SIZES;

  // The controls are a sibling of the centred table, not another flex item inside it:
  // as a third item they would push the header column off centre. Hence the wrapper,
  // which carries the vertical rhythm the table used to carry itself.
  return (
    <div className={className}>
      <div className="flex flex-shrink-0 flex-nowrap justify-center">
        <div
          className={`flex flex-col flex-shrink-0 items-start ${sizes.labelColumn}
          border-solid border-r border-[#00000014]`}
        >
          <TableHeader title="&nbsp;" price="&nbsp;" sizes={sizes} />
          {
            PRODUCT_HEADERS.map((header, index) =>
              <TableRowHeader key={index} title={header.title} description={header.description} sizes={sizes} />)
          }
          <div className={`flex items-center flex-shrink-0 self-stretch min-h-0 h-16 text-left ${sizes.labelPadding}`}>
            <strong
              className={`text-[#151012] !font-semibold leading-normal flex-1 flex-shrink-0
              ${sizes.labelText} ${sizes.tutoringLabel}`}
            >
              Private tutoring <br />
              with Mark Anestis<br />
              (live via zoom)
            </strong>

            <InfoTooltip sizes={sizes}>We will schedule these sessions at a time that works for you!</InfoTooltip>
          </div>
          <div
            className={`justify-end items-center flex-shrink-0 self-stretch
            min-h-0 h-16 text-right ${sizes.optionalRow}`}
          >
            <em className="text-footnote text-[#00000080]">Optional</em>
          </div>
        </div>
        <div className="flex overflow-y-hidden overflow-x-auto" ref={rowRef}>
          {
            PRODUCTS.map((product, index) => (
              <TableColumn
                key={product.productId}
                product={product}
                first={index === 0}
                showPrice={!renderFooter}
                footer={renderFooter?.(product)}
                sizes={sizes}
              />
            ))
          }
          {/* When the plans page, add just enough scroll range for the last plan to occupy the
              same position as every preceding plan. Wider layouts already expose multiple
              plans, so forcing that alignment there would create a large empty tail. */}
          <div aria-hidden="true" className={sizes.scrollTail} />
        </div>
      </div>

      {
        // Nothing to page => no controls at all, same rule as the video-tutorial rows. The
        // four columns fit from 1280 up (and a full-width column in compact mode), so no
        // chevrons render on a desktop.
        //
        // One class differs from the video-tutorial control: the disabled colour. The
        // `shade-*` tokens are white alphas (`--shade-30: #FFFFFF4C`) and this section is
        // `bg-common-white`, so `disabled:text-shade-30` renders an invisible chevron here --
        // which reads as "there is no control to the left" rather than "there is nothing
        // further left". `neutral-text-weak` is the palette's disabled-text colour.
        isScrollable && (
          <div className="flex justify-center mt-6">
            <button
              className="bg-green-500 text-primary p-2 disabled:text-neutral-text-weak"
              aria-label="Previous plan"
              disabled={isAtStart}
              onClick={scrollPrev}>
              <ArrowLeft width={24} height={24} />
            </button>
            <button
              className="bg-green-500 text-primary p-2 disabled:text-neutral-text-weak"
              aria-label="Next plan"
              disabled={isAtEnd}
              onClick={scrollNext}>
              <ArrowRight width={24} height={24} />
            </button>
          </div>
        )
      }
    </div>
  );
};

const InfoTooltip = ({ children, sizes }: { children: ReactNode; sizes: TableSizes }) => {
  const [visible, setVisible] = useState(false);
  const [anchor, setAnchor] = useState<HTMLButtonElement | null>();

  return (
    <>
      <button
        onClick={(event) => {
          setVisible(true);
          setAnchor(event.currentTarget);
        }}
        className={`items-center justify-center text-center text-heading
          font-bold border border-shade-10 ${sizes.infoButton}
        `}
      ><InfoCircle className={`text-[#00000080] ${sizes.infoIcon}`} /></button>
      <Popover
        width="17.25rem"
        anchor={anchor}
        title=""
        open={visible}
        onClose={() => setVisible(false)}
        placement="right-start"
        className="!p-6"
        alt
      >
        <p className="text-callout text-common-black leading-normal">
          {children}
        </p>
      </Popover>
    </>
  );
};

const TableHeader = ({ title = '', price = '', sizes }: Partial<ProductProps> & { sizes: TableSizes }) => (
  <div className="flex flex-col items-stretch">
    <strong className={`font-extraBold text-primary leading-6 ${sizes.planTitle}`}>{title}</strong>
    <em className="text-callout italic leading-normal">{price}</em>
  </div>
);

interface RowHeaderProps {
  title: string;
  description: string;
  sizes: TableSizes;
}

const TableRowHeader = ({ title, description, sizes }: RowHeaderProps) => (
  <div className={`flex items-center flex-shrink-0 self-stretch min-h-0 h-16 text-left ${sizes.labelPadding}`}>
    <strong className={`text-[#151012] !font-semibold leading-normal flex-1 flex-shrink-0 ${sizes.labelText}`}>
      {title}
    </strong>
    <InfoTooltip sizes={sizes}>{description}</InfoTooltip>
  </div>
);

interface TableCellProps {
  text: string | null;
  sizes: TableSizes;
  className?: string;
  tall?: boolean;
}

const TableCell = ({ text, sizes, className = '', tall = false }: TableCellProps) => (
  <div
    className={`flex justify-center items-center flex-shrink-0 self-stretch min-h-0
    ${tall ? sizes.tallRow : 'h-16'} ${sizes.cellPadding}`}
  >
    {text ? <span className={`text-[#151012] font-medium ${sizes.cellText} ${className}`}>{text}</span> : <Close />}
  </div>
);

interface TableColumnProps {
  product: ProductProps;
  sizes: TableSizes;
  first?: boolean;
  showPrice?: boolean;
  footer?: ReactNode;
}

const TableColumn = ({ product, sizes, first = false, showPrice = true, footer }: TableColumnProps) => (
  <div
    className={`flex flex-col flex-shrink-0 items-stretch min-w-0 ${sizes.planColumn}
    border-solid ${first ? 'border-none' : 'border-l'} border-[#00000014]`}
  >
    <TableHeader title={product.title} price={showPrice ? product.price : '\u00a0'} sizes={sizes} />
    <TableCell sizes={sizes} text={product.tests} />
    <TableCell sizes={sizes} text={product.packs} className="max-w-28" />
    <TableCell sizes={sizes} text={product.studyCenter} />
    <TableCell sizes={sizes} text={product.moduleMode} />
    <TableCell sizes={sizes} text={product.videoTutorials} />
    <TableCell sizes={sizes} text={product.privateAccess} />
    <TableCell sizes={sizes} text={product.description} tall />
    {footer && <div className={`flex flex-col items-center gap-2 pt-6 ${sizes.cellPadding}`}>{footer}</div>}
  </div>
);

// Class names per table size. Tailwind needs whole class names in the source, so each
// variant spells its classes out rather than building them.
const DEFAULT_SIZES = {
  labelColumn: 'sm:w-[260px]',
  labelPadding: 'pr-2 md:pr-6',
  labelText: 'text-callout sm:text-body',
  tutoringLabel: '-mb-12',
  optionalRow: 'hidden sm:flex pr-2 md:pr-6',
  infoButton: 'hidden sm:flex p-1 w-8 h-8 m:w-11 m:h-11',
  infoIcon: '',
  planColumn: 'w-[13.875rem]',
  planTitle: 'text-heroSubtitle',
  cellPadding: 'px-5',
  cellText: 'text-callout',
  tallRow: 'h-16',
  scrollTail: 'hidden m:block m:flex-[0_0_max(0px,calc(100%-13.875rem))]',
};

// Sized for a 40rem column: 7.5rem labels + 4 x 7.5rem plans = 37.5rem, inside the
// section's padding. The session-pricing copy takes ~5 short lines here, hence `h-24`.
// The tutoring label wraps to ~5 lines in a 7.5rem column, so instead of the default's
// pull-up it hangs from the top of its row into the (empty) pricing row below; the
// "Optional" hint that would sit there is dropped.
const COMPACT_SIZES: typeof DEFAULT_SIZES = {
  labelColumn: 'w-24 cq-md:w-[7.5rem]',
  labelPadding: 'pr-1 cq-md:pr-2',
  labelText: 'text-footnote',
  tutoringLabel: 'self-start pt-4',
  optionalRow: 'hidden',
  infoButton: 'hidden cq-md:flex p-0.5 w-6 h-6',
  infoIcon: 'w-full h-full',
  planColumn: 'w-[7.5rem]',
  planTitle: 'text-body',
  cellPadding: 'px-2',
  cellText: 'text-footnote',
  tallRow: 'h-24',
  scrollTail: 'flex-[0_0_max(0px,calc(100%-7.5rem))] cq-lg:hidden',
};

type TableSizes = typeof DEFAULT_SIZES;

export interface ProductProps {
  /** Matches `Product.productId` from the API; the ids are the same on staging and production. */
  productId: number;
  title: string;
  price: string;
  tests: string;
  packs: string;
  studyCenter: string | null;
  moduleMode: string | null;
  videoTutorials: string | null;
  privateAccess: string | null;
  description: string;
}

const PRODUCT_HEADERS = [
  {
    title: 'Practice Tests',
    // eslint-disable-next-line max-len
    description: 'There are currently 9 tests available. Idiot Tutor Mark Anestis regularly creates and adds new tests to the SATPrac site to best mimic what is showing up on recent digital SATs.',
  },
  {
    title: 'SATPrac Packs',
    // eslint-disable-next-line max-len
    description: 'These are 8-question sets that focus on specific question types. ~2,000 practice questions as well as access to super-challenging packs that will get you ready for the hardest questions you may encounter.',
  },
  {
    title: 'Study Center',
    // eslint-disable-next-line max-len
    description: 'This tool is designed to help you organize your study plan and keep tabs on which question types you need to work on more. It will help you project what you can expect to score on the actual digital SAT based on your understanding of the key fundamentals.',
  },
  {
    title: 'Module Mode',
    // eslint-disable-next-line max-len
    description: 'Module Mode allows you to attack individual digital SAT modules. (22 math questions in 35 minutes or 27 reading and writing questions in 32 minutes). Use this mode to work on your timing skills!',
  },
  {
    title: 'Video Tutorials',
    // eslint-disable-next-line max-len
    description: 'The 100+ hours of videos include: question type tutorials, question bank tutorials, test explanation tutorials, strategy tutorials, and studying tutorials',
  },
];

const PRODUCTS: ProductProps[] = [
  {
    productId: 9,
    title: 'Lite',
    price: 'Free',
    tests: '1',
    packs: '1 of each question type',
    studyCenter: null,
    moduleMode: null,
    videoTutorials: null,
    privateAccess: null,
    description: '$375/hr for a private session'
  },
  {
    productId: 27,
    title: 'Starter',
    price: '$199',
    tests: '4',
    packs: '3 of each question type',
    studyCenter: null,
    moduleMode: null,
    videoTutorials: null,
    privateAccess: null,
    description: '1 bundle of 3 sessions for $825. Additional sessions available at $350/hr.'
  },
  {
    productId: 70,
    title: 'Complete',
    price: '$799.99',
    tests: 'All available',
    packs: 'All available',
    studyCenter: 'Full Access',
    moduleMode: 'Full Access',
    videoTutorials: 'Full Access',
    privateAccess: null,
    description: '1 bundle of 3 sessions for $600. Additional sessions available at $325/hr'
  },
  {
    productId: 155,
    title: 'Concierge',
    price: '$1,349.99',
    tests: 'All available',
    packs: 'All available',
    studyCenter: 'Full Access',
    moduleMode: 'Full Access',
    videoTutorials: 'Full Access',
    privateAccess: '3 sessions included',
    description: 'Additional sessions available at $325/hr'
  },
];

// eslint-disable-next-line react-refresh/only-export-components
export const PRICING_PRODUCT_IDS = new Set(PRODUCTS.map(p => p.productId));

export default Pricing;
